import { DateField } from "@certn/cascadia-react";
import { formatDate } from "@certn/utils";
import {
  CalendarDate,
  getLocalTimeZone,
  parseDate,
  today,
} from "@internationalized/date";
import { useContext, useState } from "react";
import { DateFieldProps, DateValue } from "react-aria-components";

import { CascadiaJsonFormContext } from "../CascadiaJsonFormContext";
import { SummaryItem } from "../components/SummaryItem";
import {
  CascadiaControlProps,
  withCascadiaControlProps,
} from "../wrappers/withCascadiaControlProps";

/**
 * React Stately defaults an unset `placeholderValue` to "now", so incomplete
 * dates validate the day segment against the **current** month until month/year
 * are entered. Use a fixed 31-day month so applicants can type e.g. `31`
 * before choosing the month (DOB / task date fields).
 */
const DATE_FIELD_PLACEHOLDER_VALUE = new CalendarDate(2000, 1, 31);

const parseData = (
  data: string | undefined,
  defaultValue: DateValue | null = null
): DateValue | null => {
  if (!data) {
    return defaultValue;
  }
  try {
    return parseDate(data);
  } catch {
    return defaultValue;
  }
};

export function DateFieldControl(
  props: CascadiaControlProps &
    Pick<DateFieldProps<DateValue>, "validate" | "minValue">
) {
  const { options, path } = props;

  const [currentValue, setCurrentValue] = useState<DateValue | null>(
    parseData(props.data)
  );

  const formattedDate = formatDate(props.data, "PP", "");

  const { isSummaryForPath, formID, locale } = useContext(
    CascadiaJsonFormContext
  );

  if (
    isSummaryForPath?.(path) &&
    // some fields are displayed as readonly text boxes rather than summary items
    !props.options?.renderSummaryAsReadOnly
  ) {
    return <SummaryItem label={props.label}>{formattedDate}</SummaryItem>;
  }

  let maxValue = undefined;
  if (options.allowFutureDate === false) {
    maxValue = today(getLocalTimeZone());
  }
  const showFormattedDate = options?.showFormattedDate;

  return (
    <DateField
      errors={props.errors}
      isDisabled={!props.enabled || isSummaryForPath?.(path)}
      isInvalid={props.invalid}
      isRequired={props.required}
      label={props.label}
      description={props.options?.description}
      locale={locale}
      maxValue={maxValue as DateValue | null}
      minValue={props.minValue as DateValue | null}
      name={props.path}
      placeholderValue={DATE_FIELD_PLACEHOLDER_VALUE}
      onBlur={props.setTouched}
      onChange={(value: DateValue | null) => {
        setCurrentValue(value);
        props.setValue(value !== null ? value.toString() : undefined);
      }}
      showFormattedDate={showFormattedDate}
      validate={props.validate}
      value={currentValue}
      testID={
        formID
          ? `${formID}-${props.path}-date-field`
          : `${props.path}-date-field`
      }
    />
  );
}

DateFieldControl.displayName = "DateFieldControl";

export const DateFieldRenderer = withCascadiaControlProps(DateFieldControl);
